import { useState, useEffect } from 'react';
import { History, User, Package, Calendar, Search, ChevronDown, ChevronUp, FileSpreadsheet, Download, Trash2, Check, Truck, Car, Hash, Undo2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

export const HistoryPage = ({ privacyMode = false }) => {
    const [completedOrders, setCompletedOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);

    useEffect(() => {
        loadHistory();
    }, [selectedDate]);

    const loadHistory = async (searchTerm = '') => {
        try {
            setLoading(true);
            
            let reservations;
            if (selectedDate) {
                // Get orders by specific date
                const response = await api.get(`/orders/by-date?date=${selectedDate}`);
                reservations = response.data;
            } else {
                // Get all completed reservations
                const response = await api.get('/reservations?status=completed');
                reservations = response.data;
            }
            
            // Group by customer + order_number to separate different orders from same customer
            const grouped = {};
            for (const res of reservations) {
                const customerId = res.customer_id;
                const orderNum = res.order_number || 0; // Use 0 for orders without number
                const groupKey = `${customerId}_${orderNum}`;
                
                // Skip hidden customers in privacy mode
                if (privacyMode && res.customer?.is_hidden) continue;
                
                if (!grouped[groupKey]) {
                    grouped[groupKey] = {
                        customer: res.customer,
                        customer_id: customerId,
                        order_number: orderNum,
                        reservations: [],
                        total: 0
                    };
                }
                grouped[groupKey].reservations.push(res);
                const price = res.final_price || res.product?.price || 0;
                grouped[groupKey].total += price * res.quantity;
            }
            
            // Convert to array and filter by search
            let orders = Object.values(grouped);
            
            if (searchTerm) {
                orders = orders.filter(order => 
                    order.customer_id.toString().includes(searchTerm) ||
                    order.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    order.customer?.phone?.includes(searchTerm)
                );
            }
            
            // Sort by most recent first (by order_number desc, then by date)
            orders.sort((a, b) => {
                // First sort by order_number (higher = more recent)
                if (b.order_number !== a.order_number) {
                    return b.order_number - a.order_number;
                }
                // Then by date
                const aDate = new Date(a.reservations[a.reservations.length - 1]?.created_at || 0);
                const bDate = new Date(b.reservations[b.reservations.length - 1]?.created_at || 0);
                return bDate - aDate;
            });
            
            setCompletedOrders(orders);
        } catch (err) {
            toast.error('Eroare la încărcarea istoricului');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        loadHistory(search);
    };

    const toggleExpand = (orderKey) => {
        setExpandedCustomer(expandedCustomer === orderKey ? null : orderKey);
    };

    const handleDownloadExcel = (customerId) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        window.open(`${backendUrl}/api/export/customer-order/${customerId}?status=completed`, '_blank');
        toast.success('Se descarcă raportul Excel...');
    };

    const handleDeleteReservation = async (reservationId) => {
        if (!window.confirm('Sigur dorești să ștergi această poziție din istoric?')) return;
        
        try {
            await api.delete(`/reservations/${reservationId}`);
            toast.success('Poziție ștearsă din istoric');
            loadHistory(search);
        } catch (err) {
            toast.error('Eroare la ștergere');
            console.error(err);
        }
    };

    const handleToggleRegistered = async (reservationIds, currentState) => {
        try {
            const response = await api.put('/reservations/batch-toggle-registered', {
                reservation_ids: reservationIds
            });
            // Update local state
            setCompletedOrders(prev => prev.map(order => ({
                ...order,
                reservations: order.reservations.map(res => 
                    reservationIds.includes(res.id) 
                        ? { ...res, is_registered: response.data.is_registered }
                        : res
                )
            })));
            toast.success(response.data.is_registered ? 'Marcat ca înregistrat' : 'Dezactivat înregistrare');
        } catch (err) {
            toast.error('Eroare la actualizare');
            console.error(err);
        }
    };

    const handleToggleCourier = async (reservationIds, currentState) => {
        try {
            const response = await api.put('/reservations/batch-toggle-courier', {
                reservation_ids: reservationIds
            });
            // Update local state
            setCompletedOrders(prev => prev.map(order => ({
                ...order,
                reservations: order.reservations.map(res => 
                    reservationIds.includes(res.id) 
                        ? { ...res, courier_called: response.data.courier_called }
                        : res
                )
            })));
            toast.success(response.data.courier_called ? 'Curier chemat' : 'Dezactivat curier');
        } catch (err) {
            toast.error('Eroare la actualizare');
            console.error(err);
        }
    };

    const handleAssignOrderNumber = async (order) => {
        try {
            const reservationIds = order.reservations.map(r => r.id);
            const response = await api.put('/reservations/batch-order-number', {
                customer_id: order.customer_id,
                reservation_ids: reservationIds
            });
            
            // Update local state
            setCompletedOrders(prev => prev.map(o => {
                if (o.customer_id === order.customer_id) {
                    return {
                        ...o,
                        reservations: o.reservations.map(res => ({
                            ...res,
                            order_number: response.data.order_number
                        }))
                    };
                }
                return o;
            }));
            
            toast.success(`Comanda #${response.data.order_number} atribuită`);
        } catch (err) {
            toast.error('Eroare la atribuirea numărului de comandă');
            console.error(err);
        }
    };

    // Revert completed order back to pending
    const handleRevertOrder = async (order) => {
        try {
            // Get all reservation IDs for this order
            const reservationIds = order.reservations.map(r => r.id);
            
            await api.post('/reservations/revert-to-pending', {
                reservation_ids: reservationIds
            });
            
            toast.success('Comanda a fost readusă la Comenzi active');
            loadHistory(search);
        } catch (err) {
            toast.error('Eroare la revenirea comenzii');
            console.error(err);
        }
    };

    const totalSales = completedOrders.reduce((sum, order) => sum + order.total, 0);

    // Get today's date for default value
    const today = new Date().toISOString().split('T')[0];

    return (
        <div data-testid="history-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Istoric Comenzi
                    </h1>
                    <p className="text-zinc-500 mt-1">Comenzi finalizate</p>
                </div>
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                    <History size={20} className="text-emerald-600" />
                    <span className="font-semibold text-emerald-700">
                        Total: {totalSales.toFixed(0)} RON
                    </span>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-3">
                    {/* Date picker */}
                    <div className="flex items-center gap-2">
                        <Calendar size={18} className="text-zinc-400" />
                        <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            max={today}
                            className="w-auto"
                            data-testid="date-filter"
                        />
                        {selectedDate && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedDate('')}
                            >
                                Toate datele
                            </Button>
                        )}
                    </div>
                    
                    {/* Search */}
                    <form onSubmit={handleSearch} className="flex-1">
                        <div className="relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Caută client..."
                                className="pl-10 h-10"
                            />
                        </div>
                    </form>
                </div>
                
                {selectedDate && (
                    <p className="text-sm text-violet-600 mt-3">
                        Afișez comenzile din {new Date(selectedDate).toLocaleDateString('ro-RO', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}
                    </p>
                )}
            </div>

            {/* History List */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-6 w-32 mb-2" />
                            <Skeleton className="h-4 w-48" />
                        </div>
                    ))}
                </div>
            ) : completedOrders.length === 0 ? (
                <div className="empty-state bg-white border border-zinc-200 rounded-lg">
                    <History size={64} className="text-zinc-300" />
                    <h3 className="text-lg font-medium text-zinc-900 mt-4">
                        {selectedDate ? 'Nicio comandă în această zi' : 'Nicio comandă finalizată'}
                    </h3>
                    <p className="text-zinc-500 mt-1">
                        {selectedDate ? 'Selectează altă dată sau vezi toate comenzile' : 'Comenzile finalizate vor apărea aici'}
                    </p>
                    {selectedDate && (
                        <Button onClick={() => setSelectedDate('')} className="mt-4" variant="outline">
                            Vezi toate comenzile
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Safe Customers Section */}
                    {completedOrders.filter(o => o.customer?.is_hidden).length > 0 && (
                        <div>
                            <h2 className="text-lg font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                                <Check size={18} />
                                Clienți Siguri ({completedOrders.filter(o => o.customer?.is_hidden).length})
                            </h2>
                            <div className="space-y-3">
                                {completedOrders.filter(o => o.customer?.is_hidden).map((order) => (
                                    <OrderCard 
                                        key={`${order.customer_id}_${order.order_number}`} 
                                        order={order} 
                                        isSafeCustomer={true}
                                        expandedCustomer={expandedCustomer}
                                        toggleExpand={toggleExpand}
                                        handleAssignOrderNumber={handleAssignOrderNumber}
                                        handleToggleRegistered={handleToggleRegistered}
                                        handleToggleCourier={handleToggleCourier}
                                        handleDownloadExcel={handleDownloadExcel}
                                        handleDeleteReservation={handleDeleteReservation}
                                        handleRevertOrder={handleRevertOrder}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Customers Section */}
                    {completedOrders.filter(o => !o.customer?.is_hidden).length > 0 && (
                        <div>
                            {completedOrders.filter(o => o.customer?.is_hidden).length > 0 && (
                                <h2 className="text-lg font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                                    <Truck size={18} />
                                    Comenzi Standard ({completedOrders.filter(o => !o.customer?.is_hidden).length})
                                </h2>
                            )}
                            <div className="space-y-3">
                                {completedOrders.filter(o => !o.customer?.is_hidden).map((order) => (
                                    <OrderCard 
                                        key={`${order.customer_id}_${order.order_number}`} 
                                        order={order} 
                                        isSafeCustomer={false}
                                        expandedCustomer={expandedCustomer}
                                        toggleExpand={toggleExpand}
                                        handleAssignOrderNumber={handleAssignOrderNumber}
                                        handleToggleRegistered={handleToggleRegistered}
                                        handleToggleCourier={handleToggleCourier}
                                        handleDownloadExcel={handleDownloadExcel}
                                        handleDeleteReservation={handleDeleteReservation}
                                        handleRevertOrder={handleRevertOrder}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Separate OrderCard component for cleaner code
const OrderCard = ({ 
    order, 
    isSafeCustomer, 
    expandedCustomer, 
    toggleExpand, 
    handleAssignOrderNumber,
    handleToggleRegistered,
    handleToggleCourier,
    handleDownloadExcel,
    handleDeleteReservation,
    handleRevertOrder
}) => {
    const orderNumber = order.order_number || order.reservations[0]?.order_number;
    const isRegistered = order.reservations.some(r => r.is_registered);
    const isCourierCalled = order.reservations.some(r => r.courier_called);
    const orderKey = `${order.customer_id}_${order.order_number || 0}`;
    
    // Use Car icon for safe customers, Truck for regular
    const CourierIcon = isSafeCustomer ? Car : Truck;
    
    return (
        <div
            data-testid={`history-order-${order.customer_id}`}
            className={`bg-white border rounded-lg overflow-hidden ${
                isSafeCustomer ? 'border-emerald-300 bg-emerald-50/30' : 'border-zinc-200'
            }`}
        >
            {/* Order Header */}
            <div className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors">
                <button
                    onClick={() => toggleExpand(orderKey)}
                    className="flex items-center gap-4 flex-1 text-left"
                >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isSafeCustomer ? 'bg-emerald-200' : 'bg-emerald-100'
                    }`}>
                        <span className="font-bold text-emerald-700">{order.customer_id}</span>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            {orderNumber && (
                                <span className="bg-violet-100 text-violet-700 text-xs font-bold px-2 py-0.5 rounded">
                                    #{orderNumber}
                                </span>
                            )}
                            {order.customer?.phone && (
                                <span className="font-semibold text-zinc-900">{order.customer.phone}</span>
                            )}
                            {order.customer?.name && (
                                <span className="text-zinc-600">- {order.customer.name}</span>
                            )}
                            {isSafeCustomer && (
                                <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded flex items-center gap-1">
                                    <Check size={10} /> Sigur
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-zinc-500">
                            {order.reservations.length} produse finalizate
                        </p>
                    </div>
                </button>
                
                <div className="flex items-center gap-2">
                    {/* Order Number Button */}
                    {!orderNumber && (
                        <Button
                            onClick={() => handleAssignOrderNumber(order)}
                            variant="outline"
                            size="sm"
                            className="h-9"
                            title="Atribuie număr comandă"
                        >
                            <Hash size={14} className="mr-1" />
                            Nr.
                        </Button>
                    )}
                    
                    {/* Registration Toggle - Only for non-safe customers */}
                    {!isSafeCustomer && (
                        <button
                            onClick={() => {
                                const resIds = order.reservations.map(r => r.id);
                                handleToggleRegistered(resIds, isRegistered);
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                                isRegistered 
                                    ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                            }`}
                            title={isRegistered ? 'Înregistrat' : 'Neînregistrat'}
                        >
                            <Check size={18} />
                        </button>
                    )}
                    
                    {/* Courier Toggle - Different icon for safe customers */}
                    <button
                        onClick={() => {
                            const resIds = order.reservations.map(r => r.id);
                            handleToggleCourier(resIds, isCourierCalled);
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                            isCourierCalled 
                                ? 'bg-blue-500 text-white hover:bg-blue-600' 
                                : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'
                        }`}
                        title={isCourierCalled ? 'Curier chemat' : 'Curier nechemat'}
                    >
                        <CourierIcon size={18} />
                    </button>
                    
                    <span className="text-xl font-bold text-emerald-600 ml-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {order.total.toFixed(0)} RON
                    </span>
                    <Button
                        onClick={() => handleRevertOrder(order)}
                        variant="outline"
                        size="sm"
                        className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
                        title="Readuce la Comenzi"
                    >
                        <Undo2 size={16} />
                    </Button>
                    <Button
                        onClick={() => handleDownloadExcel(order.customer_id)}
                        variant="outline"
                        size="sm"
                        className="h-9"
                        title="Descarcă Excel"
                    >
                        <FileSpreadsheet size={16} className="mr-1" />
                        Excel
                    </Button>
                    <button onClick={() => toggleExpand(orderKey)}>
                        {expandedCustomer === orderKey ? (
                            <ChevronUp size={20} className="text-zinc-400" />
                        ) : (
                            <ChevronDown size={20} className="text-zinc-400" />
                        )}
                    </button>
                </div>
            </div>
            
            {/* Expanded Details - Grouped by Category */}
            {expandedCustomer === orderKey && (
                <div className="border-t border-zinc-200 p-4 bg-zinc-50">
                    {(() => {
                        // Group reservations by category
                        const byCategory = {};
                        order.reservations.forEach((res) => {
                            const cat = res.product?.category || 'Fără categorie';
                            if (!byCategory[cat]) {
                                byCategory[cat] = {
                                    items: [],
                                    totalQty: 0,
                                    totalValue: 0
                                };
                            }
                            const price = res.final_price || res.product?.price || 0;
                            byCategory[cat].items.push(res);
                            byCategory[cat].totalQty += res.quantity;
                            byCategory[cat].totalValue += price * res.quantity;
                        });
                        
                        return (
                            <div className="space-y-4">
                                {Object.entries(byCategory).map(([category, data]) => (
                                    <div key={category} className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
                                        {/* Category Header */}
                                        <div className="flex items-center justify-between px-3 py-2 bg-violet-50 border-b border-violet-100">
                                            <span className="font-medium text-violet-700">{category}</span>
                                            <div className="flex items-center gap-3 text-sm">
                                                <span className="text-violet-600">{data.totalQty} buc.</span>
                                                <span className="font-semibold text-violet-700">{data.totalValue.toFixed(0)} RON</span>
                                            </div>
                                        </div>
                                        
                                        {/* Products in Category */}
                                        <div className="p-2 space-y-1">
                                            {data.items.map((res) => {
                                                const price = res.final_price || res.product?.price || 0;
                                                const itemTotal = price * res.quantity;
                                                const date = res.created_at ? new Date(res.created_at).toLocaleDateString('ro-RO') : '-';
                                                
                                                return (
                                                    <div key={res.id} className="flex justify-between items-center py-1.5 px-2 hover:bg-zinc-50 rounded">
                                                        <div className="flex items-center gap-2">
                                                            <Package size={14} className="text-zinc-400" />
                                                            <span className="text-sm text-zinc-800">{res.product?.name}</span>
                                                            <span className="text-xs text-zinc-500">x{res.quantity}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-zinc-400">{date}</span>
                                                            <span className="text-sm font-medium text-zinc-700">{itemTotal.toFixed(0)} RON</span>
                                                            <button
                                                                onClick={() => handleDeleteReservation(res.id)}
                                                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                                                                title="Șterge din istoric"
                                                            >
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                    
                    {order.customer?.address && (
                        <p className="text-sm text-zinc-500 mt-3 pt-3 border-t border-zinc-200">
                            Adresă: {order.customer.address}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default HistoryPage;
